import React from "react";
import Pagination from "./Pagination";
import LoadingDataPlaceholder from "../assets/images/loading-data-placeholder.png";
import { FilterContextProvider } from "../contexts/FilterContext";
import PerPageFilter from "./filters/PerPageFilter";
import { DEFAULT_LIMIT } from "../contstants";
import "../assets/scss/components/data-table.scss";
import { useTranslation } from "react-i18next";

const DataTable = ({
  filtersTop,
  filtersBottom,
  buttons,
  columns,
  rows,
  pageCount,
  loadingData,
  error,
  actions,
  customOutputs,
  emptyDataMessage = "No data available",
}) => {
  const { t } = useTranslation();
  return (
    <>
      {filtersTop || filtersBottom || buttons ? (
        <div className="data-table__header">
          {filtersTop && <div className="filters-top">{filtersTop}</div>}
          {buttons && <div className="buttons">{buttons}</div>}
          {filtersBottom && (
            <div className="filters-bottom">{filtersBottom}</div>
          )}
        </div>
      ) : (
        ""
      )}
      {error ? (
        <div className="text-center mt-3">
          <svg
            width="80"
            height="80"
            viewBox="0 0 0.48 0.48"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              color="#000"
              d="M.193.03Q.173.029.162.034a.02.02 0 0 0-.009.011Q.15.052.151.062v.056l.002.017a.02.02 0 0 0 .009.011Q.174.151.193.15H.21v.06H.105c-.02 0-.034.012-.04.023S.06.255.06.255V.3H.043Q.023.299.012.304a.02.02 0 0 0-.009.011Q.001.322.001.332v.056l.002.017a.02.02 0 0 0 .009.011Q.024.421.043.42h.064q.02.001.031-.004A.02.02 0 0 0 .147.405Q.15.398.149.388V.332L.147.315A.02.02 0 0 0 .138.304Q.126.299.107.3H.09V.255L.092.247C.094.243.095.24.105.24h.282L.384.233A.05.05 0 0 0 .345.21H.24V.15h.017q.02.001.031-.004Q.294.143.297.135C.3.127.299.125.299.118V.062L.297.045A.02.02 0 0 0 .288.034Q.277.029.257.03zM.18.06h.09v.06H.18zM.03.33h.09v.06H.03z"
              fill="gray"
              fontFamily="sans-serif"
              fontWeight="400"
              opacity=".5"
              overflow="visible"
            />
            <path
              color="#000"
              d="M.375.27A.105.105 0 0 0 .27.375.105.105 0 0 0 .375.48.105.105 0 0 0 .48.375.105.105 0 0 0 .375.27M.36.3h.03v.035L.389.364.386.39h-.02L.362.364.361.335zm.015.12A.015.015 0 0 1 .39.435.015.015 0 0 1 .375.45.015.015 0 0 1 .36.435.015.015 0 0 1 .375.42"
              fill="#f22c42"
              overflow="visible"
            />
          </svg>
          <p className="color-error mt-2">{error}</p>
        </div>
      ) : loadingData ? (
        <img
          src={LoadingDataPlaceholder}
          className="loading-data-placeholder"
          alt={t("Loading data...")}
        />
      ) : rows?.length ? (
        <>
          <div className="data-table overflow-x-auto mb-3">
            <table>
              <thead>
                <tr>
                  {columns.map((col, index) => (
                    <th key={index} className={col.headerClassName}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((item, index) => (
                  <tr key={index}>
                    {columns.map((col, colIndex) => (
                      <td
                        key={colIndex}
                        style={
                          col.accessor === "actions" ? { width: "20px" } : {}
                        }
                      >
                        {col.accessor === "actions"
                          ? actions(item)
                          : typeof customOutputs === "function" &&
                            col.accessor in item
                          ? customOutputs(
                              col.accessor,
                              item[col.accessor],
                              item
                            )
                          : item[col.accessor]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <FilterContextProvider name="per_page">
              <PerPageFilter
                inputStyles={{ marginLeft: "10px" }}
                className="text-right"
                defaultValue={DEFAULT_LIMIT}
              />
            </FilterContextProvider>
            {pageCount > 1 && <Pagination pageCount={pageCount} />}
          </div>
        </>
      ) : (
        <div className="text-center mt-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="80"
            height="80"
          >
            <path
              fill="#010002"
              d="M3.168 19.827c.47.621 1.212.963 2.089.963h12.946c1.686 0 3.357-1.273 3.804-2.898l1.881-6.827c.233-.845.1-1.652-.372-2.272-.473-.621-1.216-.962-2.092-.962h-1.288l-.282-1.121c-.293-1.167-1.848-2.081-3.54-2.081h-4.756c-1.241 0-2.088-.254-2.29-.398-.533-.97-2.905-1.02-3.385-1.02H2.807c-.861 0-1.627.338-2.155.952s-.748 1.42-.613 2.313l2.682 12.29a2.3 2.3 0 0 0 .447 1.062M22.506 9.562c.227.298.283.713.158 1.166l-1.881 6.828c-.293 1.065-1.475 1.966-2.58 1.966H5.257c-.47 0-.853-.163-1.078-.459s-.278-.709-.151-1.162l1.293-4.594c.3-1.065 1.488-1.966 2.595-1.966h4.126c1.485 0 3.157-.538 3.477-1.563.083-.203.955-.677 2.267-.677h3.636c.472 0 .856.164 1.083.462M1.614 4.989c.283-.329.707-.51 1.193-.51h3.076c1.241 0 2.088.254 2.29.398.533.97 2.905 1.02 3.384 1.02h4.756c1.161 0 2.176.592 2.309 1.121l.204.812h-1.039c-1.486 0-3.157.538-3.477 1.563-.083.203-.955.677-2.267.677H7.917c-1.684 0-3.36 1.27-3.817 2.891l-.759 2.696-2.055-9.412c-.072-.48.044-.926.327-1.255"
            />
          </svg>
          <p className="text-center mt-3">{t(emptyDataMessage)}</p>
        </div>
      )}
    </>
  );
};

export default DataTable;
